import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import serviceManagementService, { Service, ServicePrice } from '../services/serviceManagementService';
import jobService from '../services/jobService';
import ConfirmModal from '../components/Common/ConfirmModal';
import './Services.scss';

interface Job {
  id: number;
  title: string;
}

// Custom plan pricing rates (per day / per unit)
const CUSTOM_PLAN_RATES = {
  cv_database_access: 0.50, // € per day
  job_promotion: 1.50, // € per day
  additional_job_slot: 10.00, // € per slot
};

/**
 * Soft launch — only "Darbo skelbimo iškėlimas" is sold publicly for now.
 * To bring CV database access or additional job slots back online, just add
 * their service_type to this set; both the prebuilt cards and the custom
 * plan rows below honour it.
 */
const ENABLED_SERVICE_TYPES: ReadonlySet<Service['service_type']> =
  new Set<Service['service_type']>(['job_promotion']);

const Services: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading } = useAuth();
  
  const [services, setServices] = useState<Service[]>([]);
  const [userJobs, setUserJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Selected service state for job promotion (needs job selection)
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [selectedPrice, setSelectedPrice] = useState<ServicePrice | null>(null);
  const [selectedJobId, setSelectedJobId] = useState<number | null>(null);
  const [showJobSelectModal, setShowJobSelectModal] = useState(false);
  
  // Custom plan state
  const [cvDatabaseDays, setCvDatabaseDays] = useState(0);
  const [jobPromotionDays, setJobPromotionDays] = useState(0);
  const [additionalJobSlots, setAdditionalJobSlots] = useState(0);
  
  // UI state - for validation messages
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');

  // Calculate custom plan total price
  const customPlanTotal = useMemo(() => {
    const cvPrice = cvDatabaseDays * CUSTOM_PLAN_RATES.cv_database_access;
    const promotionPrice = jobPromotionDays * CUSTOM_PLAN_RATES.job_promotion;
    const slotsPrice = additionalJobSlots * CUSTOM_PLAN_RATES.additional_job_slot;
    return cvPrice + promotionPrice + slotsPrice;
  }, [cvDatabaseDays, jobPromotionDays, additionalJobSlots]);

  const hasCustomPlanItems = cvDatabaseDays > 0 || jobPromotionDays > 0 || additionalJobSlots > 0;

  // Public-facing list — see ENABLED_SERVICE_TYPES.
  const visibleServices = useMemo(
    () => services.filter(s => ENABLED_SERVICE_TYPES.has(s.service_type)),
    [services],
  );

  const showCvDatabaseRow = ENABLED_SERVICE_TYPES.has('cv_database_access');
  const showJobPromotionRow = ENABLED_SERVICE_TYPES.has('job_promotion');
  const showAdditionalSlotsRow = ENABLED_SERVICE_TYPES.has('additional_job_slot');

  // Land at the top of the page — navigating here from the jobs list ("Iškelti")
  // otherwise inherits the previous scroll position.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!isLoading && (!isAuthenticated || user?.user_type !== 'employer')) {
      navigate('/login');
    }
  }, [isAuthenticated, user, navigate, isLoading]);

  useEffect(() => {
    if (isAuthenticated && user?.user_type === 'employer') {
      fetchServices();
      fetchUserJobs();
    }
  }, [isAuthenticated, user]);

  const fetchServices = async () => {
    try {
      setLoading(true);
      const data = await serviceManagementService.getAllServices();
      console.log('Fetched services:', data);
      
      // Handle both direct array and wrapped response
      if (Array.isArray(data)) {
        setServices(data);
      } else if ((data as any).data && Array.isArray((data as any).data)) {
        setServices((data as any).data);
      } else {
        console.error('Unexpected response format:', data);
        setServices([]);
      }
    } catch (error) {
      console.error('Error fetching services:', error);
      setModalMessage('Klaida kraunant paslaugas. Patikrinkite ar įvykdytas duomenų bazės migravimas.');
      setShowMessageModal(true);
      setServices([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserJobs = async () => {
    try {
      const jobs = await jobService.getEmployerJobs();
      setUserJobs(jobs);
    } catch (error) {
      console.error('Error fetching user jobs:', error);
    }
  };

  const handleSelectService = (service: Service, price: ServicePrice) => {
    // For job promotion, need to select a job first
    if (service.service_type === 'job_promotion') {
      if (userJobs.length === 0) {
        setModalMessage('Jūs neturite aktyvių darbo skelbimų. Pirma sukurkite skelbimą.');
        setShowMessageModal(true);
        return;
      }
      // Show job selection modal
      setSelectedService(service);
      setSelectedPrice(price);
      setSelectedJobId(null);
      setShowJobSelectModal(true);
      return;
    }
    
    // For other services, go directly to checkout
    navigateToCheckout(service, price);
  };

  const navigateToCheckout = (service: Service, price: ServicePrice, jobId?: number) => {
    const priceNum = typeof price.price === 'string' ? parseFloat(price.price) : price.price;
    
    navigate('/checkout', {
      state: {
        servicePurchase: {
          serviceId: service.id,
          serviceName: service.name,
          serviceType: service.service_type,
          priceId: price.id,
          duration: price.duration_days,
          price: priceNum,
          currency: price.currency,
          jobId: jobId
        }
      }
    });
  };

  const handleConfirmJobSelection = () => {
    if (!selectedService || !selectedPrice) return;
    
    if (!selectedJobId) {
      setModalMessage('Pasirinkite darbo skelbimą, kurį norite iškelti');
      setShowMessageModal(true);
      return;
    }
    
    setShowJobSelectModal(false);
    navigateToCheckout(selectedService, selectedPrice, selectedJobId);
  };

  const handleCancelJobSelection = () => {
    setShowJobSelectModal(false);
    setSelectedService(null);
    setSelectedPrice(null);
    setSelectedJobId(null);
  };

  const formatDuration = (days: number): string => {
    if (days === 0) return 'Vienkartinė';
    if (days === 1) return '1 diena';
    if (days === 7) return '1 savaitė';
    if (days === 14) return '2 savaitės';
    if (days === 30) return '1 mėnuo';
    if (days === 90) return '3 mėnesiai';
    if (days === 365) return '1 metai';
    return `${days} dienų`;
  };

  const formatPrice = (price: number | string, currency: string): string => {
    const priceNum = typeof price === 'string' ? parseFloat(price) : price;
    return `${priceNum.toFixed(2)} ${currency}`;
  };

  const getPricePerDay = (price: number | string, days: number): string => {
    if (days === 0) return '';
    const priceNum = typeof price === 'string' ? parseFloat(price) : price;
    const perDay = priceNum / days;
    return `(${perDay.toFixed(2)} €/dieną)`;
  };

  const handleCheckout = () => {
    if (!hasCustomPlanItems) {
      setModalMessage('Pasirinkite bent vieną paslaugą');
      setShowMessageModal(true);
      return;
    }
    
    // Navigate to checkout with custom plan data
    navigate('/checkout', {
      state: {
        customPlan: {
          cvDatabaseDays,
          jobPromotionDays,
          additionalJobSlots,
          total: customPlanTotal,
          breakdown: {
            cvDatabase: cvDatabaseDays * CUSTOM_PLAN_RATES.cv_database_access,
            jobPromotion: jobPromotionDays * CUSTOM_PLAN_RATES.job_promotion,
            additionalSlots: additionalJobSlots * CUSTOM_PLAN_RATES.additional_job_slot,
          }
        }
      }
    });
  };

  if (loading) {
    return (
      <div className="services-page">
        <div className="container">
          <p>Kraunama...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="services-page">
      <div className="container">
        <div className="breadcrumbs">
          <span onClick={() => navigate('/?mode=employer')} className="breadcrumb-link">Titulinis</span>
          <span className="breadcrumb-separator"> &gt; </span>
          <span className="breadcrumb-current">Paslaugos</span>
        </div>

        {services.length === 0 ? (
          <div className="no-services">
            <h3>Paslaugos dar neįkeltos</h3>
            <p>Prašome įvykdyti duomenų bazės migravimą:</p>
            <pre>cd backend && node run-migrations.js</pre>
            <p>Arba rankiniu būdu:</p>
            <pre>mysql -u root -p gidas_lt &lt; database/migrations/017_create_services_tables.sql</pre>
          </div>
        ) : (
          <div
            className={`services-grid${
              visibleServices.length === 1 ? ' services-grid--single' : ''
            }`}
          >
            {visibleServices.map((service) => (
            <div key={service.id} className="service-card">
              <div className="service-header">
                <h3>{service.name}</h3>
                <p className="service-description">{service.description}</p>
              </div>
              
              <div className="service-prices">
                {service.prices.map((price) => (
                  <div key={price.id} className="price-option">
                    <div className="price-info">
                      <div className="duration">{formatDuration(price.duration_days)}</div>
                      <div className="price">
                        {formatPrice(price.price, price.currency)}
                        {price.duration_days > 0 && (
                          <span className="price-per-day">{getPricePerDay(price.price, price.duration_days)}</span>
                        )}
                      </div>
                    </div>
                    <button
                      className="btn-purchase"
                      onClick={() => handleSelectService(service, price)}
                    >
                      Pirkti
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
          </div>
        )}

        {/* Divider */}
        <div className="section-divider">
          <span>arba sukurkite savo planą</span>
        </div>

        {/* Custom Plan Builder - Minimalistic */}
        <div className="custom-plan-section">
          <h3>Sukurkite savo planą</h3>
          
          <div className="custom-plan-builder">
            {showCvDatabaseRow && (
              <div className="plan-row">
                <div className="plan-label">
                  <span className="plan-name">CV duomenų bazės prieiga</span>
                  <span className="plan-rate">0.50 €/d.</span>
                </div>
                <div className="plan-slider-wrap">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={cvDatabaseDays}
                    onChange={(e) => setCvDatabaseDays(Number(e.target.value))}
                    className="plan-slider"
                  />
                  <span className="plan-value">{cvDatabaseDays} d.</span>
                </div>
                <div className="plan-price">
                  {(cvDatabaseDays * CUSTOM_PLAN_RATES.cv_database_access).toFixed(2)} €
                </div>
              </div>
            )}

            {showJobPromotionRow && (
              <div className="plan-row">
                <div className="plan-label">
                  <span className="plan-name">Darbo skelbimo iškėlimas</span>
                  <span className="plan-rate">1.50 €/d.</span>
                </div>
                <div className="plan-slider-wrap">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={jobPromotionDays}
                    onChange={(e) => setJobPromotionDays(Number(e.target.value))}
                    className="plan-slider"
                  />
                  <span className="plan-value">{jobPromotionDays} d.</span>
                </div>
                <div className="plan-price">
                  {(jobPromotionDays * CUSTOM_PLAN_RATES.job_promotion).toFixed(2)} €
                </div>
              </div>
            )}

            {showAdditionalSlotsRow && (
              <div className="plan-row">
                <div className="plan-label">
                  <span className="plan-name">Papildoma skelbimo vieta</span>
                  <span className="plan-rate">10.00 €/vnt.</span>
                </div>
                <div className="plan-slider-wrap">
                  <input
                    type="range"
                    min="0"
                    max="10"
                    value={additionalJobSlots}
                    onChange={(e) => setAdditionalJobSlots(Number(e.target.value))}
                    className="plan-slider"
                  />
                  <span className="plan-value">{additionalJobSlots} vnt.</span>
                </div>
                <div className="plan-price">
                  {(additionalJobSlots * CUSTOM_PLAN_RATES.additional_job_slot).toFixed(2)} €
                </div>
              </div>
            )}

            <div className="plan-footer">
              <div className="plan-total">
                <span>Viso:</span>
                <span className="total-amount">{customPlanTotal.toFixed(2)} €</span>
              </div>
              <button 
                className="btn-checkout"
                onClick={handleCheckout}
                disabled={!hasCustomPlanItems}
              >
                Pirkti
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Job Selection Modal for Job Promotion */}
      {showJobSelectModal && selectedService && selectedPrice && (
        <div className="modal-overlay" onClick={handleCancelJobSelection}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Pasirinkite skelbimą</h3>
            </div>
            <div className="modal-body">
              <p>Pasirinkite darbo skelbimą, kurį norite iškelti:</p>
              <select 
                className="job-select"
                value={selectedJobId || ''}
                onChange={(e) => setSelectedJobId(Number(e.target.value))}
              >
                <option value="">Pasirinkite skelbimą</option>
                {userJobs.map(job => (
                  <option key={job.id} value={job.id}>{job.title}</option>
                ))}
              </select>
              <div className="confirm-details">
                <p>Paslauga: <strong>{selectedService.name}</strong></p>
                <p>Trukmė: <strong>{formatDuration(selectedPrice.duration_days)}</strong></p>
                <p>Kaina: <strong>{formatPrice(selectedPrice.price, selectedPrice.currency)}</strong></p>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={handleCancelJobSelection}>
                Atšaukti
              </button>
              <button className="btn-primary" onClick={handleConfirmJobSelection}>
                Tęsti
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Message Modal */}
      <ConfirmModal
        isOpen={showMessageModal}
        message={modalMessage}
        confirmText="Gerai"
        cancelText="Uždaryti"
        onConfirm={() => setShowMessageModal(false)}
        onCancel={() => setShowMessageModal(false)}
      />
    </div>
  );
};

export default Services;

